import {assert, assertAndContinue} from 'Asserts';
import {isWellSample, SampleDetails} from 'constants/SampleConstants';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import APIUtil from 'util/APIUtil';

import {SampleFilterType} from '../SampleFilter';
import getSampleId from './getSampleId';
import makeSampleCell, {makeGridSampleCell, makePositionSampleCell} from './makeSampleCell';
import makeSampleDetailsFromSampleIds from './makeSampleDetailsFromSampleIds';

type SampleCellsByColumn = Array<{cells: Array<Cell>; columnDefinition: ColumnDefinition}>;

/**
  Creates an array of cells that represent samples. The cells will be created so that they match
  the column definition of the destination column.

  This function expects the sample details to already be present in the store. No data fetching
  will occur here!

  Must be kept in sync with `getAcceptableSampleFiltersByColumn`, which filters destination
  columns by what type of sample they can accept.
*/
// This function cannot accept well samples, so we only have a singleton columnDefinition
export function makeSampleCellsFromIds(
  sampleIds: Array<string>,
  sampleType: SampleFilterType,
  columnDefinition: ColumnDefinition
): SampleCellsByColumn {
  assert(sampleType !== SampleFilterType.WELL, 'makeSampleCellsFromIds cannot accept well samples');
  return makeSampleCells(makeSampleDetailsFromSampleIds(sampleIds), sampleType, [columnDefinition]);
}

// The result array should persist the ordering of the input columnDefinitions for well insertion
export default function makeSampleCells(
  samples: Array<SampleDetails>,
  sampleType: SampleFilterType,
  columnDefinitions: Array<ColumnDefinition>
): SampleCellsByColumn {
  const cells: Array<Cell> = [];
  const positionCells: Array<Cell> = [];
  if (sampleType === SampleFilterType.WELL) {
    assert(columnDefinitions.length === 2, 'Must provide exactly 2 columnDefinitions for wells insertion');
  } else {
    assert(columnDefinitions.length === 1, 'Must provide a single columnDefinition for non-well insertion');
  }
  const columnDefinition = columnDefinitions[0];

  // Ensure that the samples that were created match the desired column type
  for (const sample of samples) {
    const sampleId = getSampleId(sample)!;
    let cell: Cell | null = null;
    let positionCell: Cell | null = null; // Only used for well insertion
    if (sampleType === SampleFilterType.BIOENTITY) {
      if (
        assertAndContinue(
          APIUtil.isBiologicalEntity(sampleId),
          'Trying to insert a non-BIOENTITY sample into a bioentity column',
          {sampleId, sampleType, columnDefinition}
        )
      ) {
        cell = makeSampleCell(columnDefinition, sample);
      }
    } else if (
      [
        SampleFilterType.STRING,
        SampleFilterType.FLOAT,
        SampleFilterType.INTEGER,
        SampleFilterType.BOOLEAN,
      ].includes(sampleType)
    ) {
      cell = makeSampleCell(columnDefinition, sample);
    } else if (
      [SampleFilterType.BOX, SampleFilterType.CONTAINER, SampleFilterType.PLATE].includes(sampleType)
    ) {
      const isStorableLink =
        columnDefinition.type === TableColumnType.TAG_VALUE &&
        columnDefinition.tagSchemaFieldType === FieldType.STORABLE_LINK;
      const isBoxColumn =
        APIUtil.isBox(sampleId) && (columnDefinition.type === TableColumnType.BOX || isStorableLink);
      const isContainerColumn =
        APIUtil.isContainer(sampleId) &&
        (columnDefinition.type === TableColumnType.CONTAINERS || isStorableLink);
      const isPlateColumn =
        APIUtil.isPlate(sampleId) && (columnDefinition.type === TableColumnType.PLATE || isStorableLink);

      assert(
        isBoxColumn || isContainerColumn || isPlateColumn,
        `Trying to insert a non-${sampleType} sample into a ${columnDefinition.type} column`,
        {sampleId, sampleType, columnDefinition}
      );
      cell = makeSampleCell(columnDefinition, sample);
    } else if (sampleType === SampleFilterType.WELL) {
      assert(isWellSample(sample), 'SampleDetail must be a well sample when sampleType is well', {
        columnDefinition,
        sample,
      });
      assert(
        sample.plateInfo != null && sample.position != null,
        'Well sample requires plateInfo and position',
        {
          columnDefinition,
          sample,
        }
      );
      cell = makeGridSampleCell(sample.plateInfo);
      positionCell = makePositionSampleCell(sample.position);
    }
    if (cell) {
      cells.push(cell);
    }
    if (positionCell) {
      positionCells.push(positionCell);
    }
  }
  assert(
    cells.length === positionCells.length || sampleType !== SampleFilterType.WELL,
    'Must have equal number of plate cells and position cells when inserting wells'
  );

  return sampleType === SampleFilterType.WELL
    ? columnDefinitions.map((columnDefinition) => ({
        cells: columnDefinition.type === TableColumnType.PLATE ? cells : positionCells,
        columnDefinition,
      }))
    : [{cells, columnDefinition}];
}
